import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import PublicShell from "../components/PublicShell";
import { Button } from "../components/ui/button";
import { Sparkles, Globe2, ShieldCheck, Heart, Compass, Mountain } from "lucide-react";

const VALUES = [
  {
    icon: Globe2,
    title: "Global by default",
    blurb:
      "Every workflow is built for delegates from 80+ nationalities — currencies, time zones, languages and curricula are first-class citizens.",
  },
  {
    icon: ShieldCheck,
    title: "Trust as a moat",
    blurb:
      "QR-verified certificates, transparent voting audit logs and SOC-2 readiness aren't features — they're how we earn the right to your data.",
  },
  {
    icon: Heart,
    title: "Student-first",
    blurb:
      "Every product decision starts with a 14-year-old delegate giving their first opening speech. If it doesn't make them feel powerful, we cut it.",
  },
  {
    icon: Compass,
    title: "Built with chairs",
    blurb:
      "Half our roadmap comes from working chairs and EBs across 200+ MUNs. They run our beta cohorts and ship features they actually use.",
  },
  {
    icon: Mountain,
    title: "Long-game leadership",
    blurb:
      "Conferences are the start, not the end. The Delegate Passport turns one MUN into a lifelong global leadership profile.",
  },
];

const TIMELINE = [
  { year: "2023", label: "First lines of code", note: "Two debaters and a chair start writing the kernel." },
  { year: "2024", label: "First 1000 delegates", note: "Six pilot MUNs, zero downtime, first ovation." },
  { year: "2025", label: "AI evaluation goes live", note: "Diplomexa AI paper grading replaces 40 hours of chair work." },
  { year: "2026", label: "Global Conference OS", note: "Delegate passport, sponsor portal & multi-event seasons." },
];

const NUMBERS = [
  ["18,000+", "Delegates onboarded"],
  ["240+", "Councils simulated"],
  ["32", "Partner schools"],
  ["18", "Countries reached"],
  ["4.9★", "Organiser rating"],
  ["0", "Cancelled conferences"],
];

export default function AboutPage() {
  return (
    <PublicShell>
      <section className="relative overflow-hidden bg-brand-navy-dark text-brand-champagne">
        <div className="absolute inset-0 opacity-25 bg-cover bg-center"
          style={{ backgroundImage: "url('https://images.unsplash.com/photo-1528747045269-390fe33c19f2')" }} />
        <div className="absolute inset-0 bg-gradient-to-br from-brand-navy-dark via-brand-navy/95 to-brand-navy-dark/85" />
        <div aria-hidden className="bg-noise absolute inset-0 pointer-events-none" />
        <div aria-hidden className="absolute -top-24 -right-24 w-[28rem] h-[28rem] rounded-full bg-brand-gold/10 blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-6 py-24 lg:py-28">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-sm bg-white/5 border border-brand-gold/30 mb-6">
            <Sparkles className="h-3 w-3 text-brand-gold" />
            <span className="eyebrow">About Diplomexa</span>
          </div>
          <h1 className="headline-luxury text-4xl sm:text-5xl lg:text-[4.5rem] text-brand-champagne max-w-4xl">
            From conferences to{" "}
            <span className="italic font-display font-medium text-brand-gold">lifelong</span>{" "}
            global leadership journeys.
          </h1>
          <div className="divider-gold-hairline mt-8 max-w-md" />
          <p className="mt-8 text-base lg:text-lg text-brand-champagne/70 max-w-2xl leading-relaxed">
            We&apos;re a team of former chairs, organisers and engineers building the
            operating system that should have always existed for student-led
            global affairs — one that respects the dignity of the dais and the
            ambition of every delegate.
          </p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 py-20 lg:py-24">
        <div className="grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <p className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold">
              Mission
            </p>
            <h2 className="mt-4 text-3xl sm:text-4xl font-bold tracking-tighter text-[#0A192F] leading-tight">
              Make every <span className="font-serif italic text-[#0F4C81]">student-led conference</span> feel like the United Nations.
            </h2>
          </div>
          <div className="lg:col-span-7 text-slate-700 text-base leading-relaxed space-y-4">
            <p>
              In 2023 our co-founders ran a 600-delegate MUN with eight Google
              Sheets, three WhatsApp groups, two Notion pages and a Razorpay
              dashboard taped together with willpower. The conference was beautiful.
              The backstage was a nightmare.
            </p>
            <p>
              We knew the chairs deserved better. The delegates deserved better.
              The schools writing cheques deserved better. So we set out to build
              one piece of software that respected every stakeholder: the secretariat
              running the show, the chair scoring the dais, the parent paying the fee
              and the delegate giving their first speech.
            </p>
            <p>
              Today Diplomexa powers MUNs, debate championships, youth parliaments
              and SDG summits across 18 countries — and we're just getting started.
              Our north star: a billion confident young leaders, each carrying a
              global passport of their conference journey.
            </p>
          </div>
        </div>
      </section>

      <section className="bg-[#0A192F] text-white">
        <div className="max-w-7xl mx-auto px-6 py-16">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6 text-center">
            {NUMBERS.map(([k, v]) => (
              <div key={v}>
                <p className="font-serif text-3xl lg:text-4xl text-[#D4AF37]">{k}</p>
                <p className="text-[10px] uppercase tracking-[0.22em] text-slate-400 mt-2">{v}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Company information block */}
      <section className="border-y border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto px-6 py-16">
          <div className="grid lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-5">
              <p className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold">
                The Company
              </p>
              <h2 className="mt-3 text-3xl sm:text-4xl font-bold tracking-tighter text-[#0A192F] leading-tight">
                A product of <span className="font-serif italic text-[#0F4C81]">Factskoot Eduwellness Private Limited</span>.
              </h2>
              <p className="mt-5 text-base text-slate-600 leading-relaxed">
                Factskoot Eduwellness Pvt. Ltd. is a youth education and student-leadership
                company building learning experiences, conferences and digital platforms
                for the next generation of global citizens. Diplomexa is our flagship
                operating system for student-led conferences worldwide.
              </p>
            </div>
            <div className="lg:col-span-7 grid sm:grid-cols-2 gap-3" data-testid="company-info-grid">
              <CompanyInfo label="Legal name" value="Factskoot Eduwellness Private Limited" />
              <CompanyInfo label="Headquarters" value="Bengaluru, India" />
              <CompanyInfo label="Website" value="www.factskoot.com" href="https://www.factskoot.com" />
              <CompanyInfo label="General enquiries" value="info@factskoot.com" href="mailto:info@factskoot.com" />
              <CompanyInfo label="Phone & WhatsApp" value="+91 98441 39333" href="tel:+919844139333" />
              <CompanyInfo label="Product" value="Diplomexa — The Global Conference & Leadership OS" />
              <CompanyInfo label="Industry" value="EdTech · Conference Software · Youth Leadership" />
              <CompanyInfo label="Established" value="2023 · Bengaluru, India" />
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 py-20">
        <div className="max-w-3xl">
          <p className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold">
            What we believe
          </p>
          <h2 className="mt-4 text-3xl sm:text-4xl font-bold tracking-tighter text-[#0A192F]">
            Five principles that <span className="font-serif italic text-[#0F4C81]">never bend</span>.
          </h2>
        </div>
        <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {VALUES.map((v, i) => {
            const Icon = v.icon;
            return (
              <motion.div
                key={v.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className="rounded-xl border border-slate-200 bg-white p-6"
              >
                <div className="h-10 w-10 rounded-md bg-[#0A192F] text-[#D4AF37] flex items-center justify-center">
                  <Icon className="h-5 w-5" />
                </div>
                <h3 className="mt-5 text-lg font-semibold tracking-tight text-[#0A192F]">{v.title}</h3>
                <p className="mt-3 text-sm text-slate-600 leading-relaxed">{v.blurb}</p>
              </motion.div>
            );
          })}
        </div>
      </section>

      <section className="bg-[#f6f8fb] border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <div className="max-w-3xl">
            <p className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold">
              Our journey
            </p>
            <h2 className="mt-4 text-3xl sm:text-4xl font-bold tracking-tighter text-[#0A192F]">
              From a single MUN to a <span className="font-serif italic text-[#0F4C81]">global platform</span>.
            </h2>
          </div>
          <div className="mt-12 grid md:grid-cols-4 gap-5">
            {TIMELINE.map((t) => (
              <div key={t.year} className="rounded-xl bg-white border border-slate-200 p-6">
                <p className="font-serif text-3xl text-[#D4AF37]">{t.year}</p>
                <p className="mt-3 text-base font-semibold text-[#0A192F] tracking-tight">{t.label}</p>
                <p className="mt-2 text-sm text-slate-600">{t.note}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-[#0A192F]" />
        <div className="relative max-w-5xl mx-auto px-6 py-20 text-center">
          <h2 className="text-3xl sm:text-5xl font-bold tracking-tighter text-white">
            Build the future of <span className="gold-gradient-text font-serif italic">student diplomacy</span> with us.
          </h2>
          <p className="mt-5 text-slate-300 max-w-2xl mx-auto">
            Whether you run a 50-delegate intraschool MUN or a national debate
            circuit, we want to power it. The first conference is on us.
          </p>
          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <Link to="/register">
              <Button className="h-12 px-6 bg-[#D4AF37] text-[#0A192F] hover:bg-[#B5952F] font-bold uppercase tracking-[0.18em]" data-testid="about-register-cta">
                Start your first event
              </Button>
            </Link>
            <Link to="/contact">
              <Button variant="outline" className="h-12 px-6 border-white/30 bg-transparent text-white hover:bg-white/10 hover:text-white" data-testid="about-contact-cta">
                Talk to a founder
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </PublicShell>
  );
}

function CompanyInfo({ label, value, href }) {
  const Inner = (
    <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 hover:border-[#0F4C81] transition-colors">
      <p className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">{label}</p>
      <p className="mt-1.5 text-sm font-semibold text-[#0A192F]">{value}</p>
    </div>
  );
  return href ? (
    <a href={href} target={href.startsWith("http") ? "_blank" : undefined} rel="noreferrer" className="block">
      {Inner}
    </a>
  ) : Inner;
}
